import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../lib/auth-context';
import { Document } from '../types/Document';
import {
  updateDocumentProcessingResults,
  checkJobStatus,
  getJobResult,
} from '../lib/api-client';
import { API_URL, FASTAPI_URL } from '@/lib/config';

const normalizeProcessingStatus = (raw: unknown): string => {
  const s = String(raw || '');
  if (s === 'not_found') return 'error';
  return s || 'processing';
};

const processingStatusRank = (status: string | null): number => {
  switch (status) {
    case 'not_processed': return 0;
    case 'preparing': return 1;
    case 'uploading': return 2;
    case 'queued': return 3;
    case 'in_progress': return 4;
    case 'processing': return 4;
    case 'done': return 5;
    case 'error': return 5;
    default: return 0;
  }
};

const isTerminalProcessingStatus = (status: string | null) =>
  status === 'done' || status === 'error';

const shouldAcceptProcessingStatus = (prev: string | null, next: string): boolean => {
  if (!prev) return true;
  if (isTerminalProcessingStatus(prev)) return false;
  if (next === 'done' || next === 'error') return true;
  return processingStatusRank(next) >= processingStatusRank(prev);
};

export interface UseDocumentProcessingReturn {
  processing: boolean;
  message: string;
  error: string;
  handleProcess: (document: Document | null, documentId: string, fetchDocument: () => Promise<void>) => Promise<void>;
  clearMessages: () => void;
}

export const useDocumentProcessing = (): UseDocumentProcessingReturn => {
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inFlightRef = useRef(false);
  const requestSeqRef = useRef(0);
  const lastAcceptedStatusRef = useRef<string | null>(null);
  const processingRef = useRef(false);

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  // Polling function needs to be defined within the hook or passed in if it uses hook state
  const startPolling = useCallback(async (documentId: string, jobId: string, fetchDoc: () => Promise<void>) => {
    // Ensure we never have multiple intervals for the same doc in this view.
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    const pollInterval = setInterval(async () => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const reqId = ++requestSeqRef.current;
      try {
        const statusResponse = await checkJobStatus(jobId);
        // Ignore out-of-order responses
        if (requestSeqRef.current !== reqId) return;

        const normalizedStatus = normalizeProcessingStatus((statusResponse as any)?.status);
        if (!shouldAcceptProcessingStatus(lastAcceptedStatusRef.current, normalizedStatus)) return;
        lastAcceptedStatusRef.current = normalizedStatus;

        await updateDocumentProcessingResults(documentId, { status: normalizedStatus });

        if (normalizedStatus === 'done') {
          const resultResponse = await getJobResult(jobId);
          const updateData: { status: string; result: any; warnings?: string[] } = {
            status: 'done',
            result: resultResponse.result,
          };
          // Forward warnings from the processing API if present
          if (Array.isArray(resultResponse.warnings) && resultResponse.warnings.length > 0) {
            updateData.warnings = resultResponse.warnings;
          }
          await updateDocumentProcessingResults(documentId, updateData);
          clearInterval(pollInterval);
          if (pollIntervalRef.current) pollIntervalRef.current = null;
          fetchDoc(); // Refresh document data and results
          setMessage('Document processing completed successfully');
          setTimeout(() => setMessage(''), 5000);
        } else if (normalizedStatus === 'error') {
          await updateDocumentProcessingResults(documentId, { status: 'error' });
          clearInterval(pollInterval);
          if (pollIntervalRef.current) pollIntervalRef.current = null;
          fetchDoc(); // Refresh document data
          setError(`Processing failed: ${normalizedStatus}`);
          setTimeout(() => setError(''), 5000);
        }
      } catch (err) {
        console.error('Error checking job status:', err);
        try {
          await updateDocumentProcessingResults(documentId, { status: 'error' });
        } catch (updateErr) {
          console.error('Error updating document status to error during polling:', updateErr);
        }
        clearInterval(pollInterval);
        if (pollIntervalRef.current) pollIntervalRef.current = null;
        fetchDoc(); // Refresh document data
      } finally {
        inFlightRef.current = false;
      }
    }, 2000); // Poll every 2 seconds
    pollIntervalRef.current = pollInterval;
    return pollInterval;
  }, []); // No dependencies needed for startPolling itself, but it uses args passed to it

  const handleProcess = useCallback(async (document: Document | null, documentId: string, fetchDoc: () => Promise<void>) => {
    if (!document || !documentId || !user?.token) return;
    if (processingRef.current) return;

    setProcessing(true);
    setError('');
    setMessage('');

    try {
      await updateDocumentProcessingResults(documentId, { status: 'preparing' });

      const response = await fetch(`${API_URL}/documents/${documentId}/content`, {
        headers: { 'Authorization': `Bearer ${user.token}` },
      });

      if (!response.ok) throw new Error('Failed to download document for processing');
      const blob = await response.blob();
      const file = new File([blob], document.originalName, { type: document.type });

      await updateDocumentProcessingResults(documentId, { status: 'uploading' });

      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(`${FASTAPI_URL}/process`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${user.token}` }, // Assuming token is needed for FastAPI as well
        body: formData,
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload document to processing server');
      const uploadData = await uploadResponse.json();
      const jobId = uploadData.job_id;

      await updateDocumentProcessingResults(documentId, {
        status: uploadData.status,
        jobId: jobId,
      });

      startPolling(documentId, jobId, fetchDoc);
      setMessage('Document processing started successfully');
      setTimeout(() => setMessage(''), 5000);
      fetchDoc(); // Initial refresh after starting process
    } catch (err) {
      console.error('Error processing document:', err);
      setError(err instanceof Error ? err.message : 'Error processing document');
      try {
        await updateDocumentProcessingResults(documentId, { status: 'error' });
      } catch (updateErr) {
        console.error('Error updating document status to error:', updateErr);
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessing(false);
    }
  }, [user?.token, startPolling]); // Added startPolling to dependencies

  return {
    processing,
    message,
    error,
    handleProcess,
    clearMessages,
  };
}; 